import React, { useState } from 'react';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import GitHubIcon from '@material-ui/icons/GitHub';
import { useBitriseBuilds } from '../../hooks/useBitriseBuilds.esm.js';
import { BitriseBuildResultStatus } from '../../api/bitriseApi.model.esm.js';
import { BitriseBuildDetailsDialog } from '../BitriseBuildDetailsDialog/BitriseBuildDetailsDialog.esm.js';
import { DateTime } from 'luxon';
import { Table, SubvalueCell, StatusWarning, StatusAborted, StatusRunning, StatusError, StatusOK, Link } from '@backstage/core-components';

const bitriseBaseUrl = `https://app.bitrise.io`;
const createBitriseBuildUrl = (buildSlug) => `${bitriseBaseUrl}/build/${buildSlug}#?tab=log`;
const renderBranch = (build) => {
  return /* @__PURE__ */ React.createElement(SubvalueCell, { value: build.message, subvalue: build.workflow });
};
const renderStatus = (build) => {
  switch (build.status) {
    case BitriseBuildResultStatus.successful: {
      return /* @__PURE__ */ React.createElement(StatusOK, null, build.statusText);
    }
    case BitriseBuildResultStatus.failed: {
      return /* @__PURE__ */ React.createElement(StatusError, null, build.statusText);
    }
    case BitriseBuildResultStatus.notFinished: {
      return /* @__PURE__ */ React.createElement(StatusRunning, null, build.statusText);
    }
    case BitriseBuildResultStatus.abortedWithSuccess:
    case BitriseBuildResultStatus.abortedWithFailure: {
      return /* @__PURE__ */ React.createElement(StatusAborted, null, build.statusText);
    }
    default: {
      return /* @__PURE__ */ React.createElement(StatusWarning, null, build.statusText);
    }
  }
};
const renderSource = (build) => {
  return build.source ? /* @__PURE__ */ React.createElement(
    Button,
    {
      href: build.source,
      target: "_blank",
      rel: "noreferrer",
      startIcon: /* @__PURE__ */ React.createElement(GitHubIcon, null)
    },
    build.commitHash.slice(0, 6)
  ) : null;
};
const renderId = (build) => {
  return /* @__PURE__ */ React.createElement(
    Link,
    {
      to: `${createBitriseBuildUrl(build.buildSlug)}`,
      target: "_blank",
      rel: "noreferrer"
    },
    build.id
  );
};
const renderDownload = (build) => {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, build.status === BitriseBuildResultStatus.successful && /* @__PURE__ */ React.createElement(BitriseBuildDetailsDialog, { build }));
};
const renderTriggerTime = (build) => {
  return /* @__PURE__ */ React.createElement(
    SubvalueCell,
    {
      value: DateTime.fromISO(build.triggerTime).toLocaleString(
        DateTime.DATETIME_MED
      ),
      subvalue: build.status !== BitriseBuildResultStatus.notFinished && build.duration
    }
  );
};
const renderErrors = (errors) => {
  return errors.map(
    (err) => err ? /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, err.message) : null
  );
};
const BitriseBuildsTable = ({
  appName,
  workflow,
  error
}) => {
  let selectedWorkflow = "";
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [{ page, next }, setPage] = React.useState({ page: 0 });
  const [selectedSearchTerm, setSelectedSearchTerm] = useState("");
  if (workflow) {
    selectedWorkflow = workflow;
  }
  const builds = useBitriseBuilds(appName, {
    workflow: selectedWorkflow.trim(),
    branch: selectedSearchTerm.trim(),
    limit: rowsPerPage,
    next
  });
  const columns = [
    {
      title: "ID",
      field: "id",
      highlight: true,
      render: (build) => renderId(build)
    },
    {
      title: "Branch",
      customFilterAndSearch: (query, row) => `${row.message} ${row.workflow}`.toLocaleUpperCase("en-US").includes(query.toLocaleUpperCase("en-US")),
      field: "message",
      width: "auto",
      highlight: true,
      render: (build) => renderBranch(build)
    },
    {
      title: "Status",
      field: "status",
      render: (build) => renderStatus(build)
    },
    {
      title: "Triggered",
      field: "triggered",
      render: (build) => renderTriggerTime(build)
    },
    {
      title: "Source",
      field: "source",
      render: (build) => renderSource(build)
    },
    {
      title: "Download",
      field: "download",
      render: (build) => renderDownload(build)
    }
  ];
  const handleChangePage = (newPage, nextId) => {
    if (nextId) {
      setPage({ page: newPage, next: nextId });
    }
  };
  const handleChangeRowsPerPage = (amount) => {
    setRowsPerPage(amount);
    setPage({ page: 0, next: void 0 });
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, renderErrors([builds.error, error]), !builds.error && /* @__PURE__ */ React.createElement(
    Table,
    {
      isLoading: builds.loading,
      options: {
        search: true,
        searchAutoFocus: true,
        debounceInterval: 800,
        paging: true,
        showFirstLastPageButtons: false,
        pageSize: rowsPerPage,
        pageSizeOptions: [5, 10, 20, 50]
      },
      totalCount: builds.value?.paging?.total_item_count || 0,
      page,
      onPageChange: (pageIndex) => {
        handleChangePage(pageIndex, builds.value?.paging?.next);
      },
      onRowsPerPageChange: handleChangeRowsPerPage,
      columns,
      data: builds.value?.data || [],
      onSearchChange: (searchTerm) => setSelectedSearchTerm(searchTerm)
    }
  ));
};

export { BitriseBuildsTable };
//# sourceMappingURL=BitriseBuildsTableComponent.esm.js.map
